import { startOfZonedDay, zonedDateKey, type CapacityDto } from '@dbrain/shared';
import { Icons, Menu, MenuItem, Progress } from '@dbrain/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client.js';
import { useCompanyZone } from '../../api/hooks.js';
import { formatBytes, formatPercent } from '@dbrain/app-core/format';
import { ErrorAlert } from '../ErrorAlert.js';

/**
 * Kapasite (Phase 13g, D-138; KurumsalKapasite): the installation's load against the Phase 7 measurement, from
 * real numbers only. Four cards (the model server, active users, code jobs, first response), one sentence on
 * whether it is enough, the chosen day hour by hour against the measured band, and the server's resources.
 * The kurumsal super admin's Modeller → Kapasite and the bireysel operator's Platform → Kapasite.
 */
export type CapacityDay = 'today' | 'yesterday';

/** "Dönem: Bugün ⌄" */
export function CapacityDaySelect({ value, onChange }: { value: CapacityDay; onChange: (day: CapacityDay) => void }) {
  const { t } = useTranslation();
  return (
    <Menu
      trigger={
        <>
          <span>{t('capacity.period', { day: t(`capacity.days.${value}`) })}</span>
          <Icons.chevronDown size={14} />
        </>
      }
      align="right"
      triggerClassName="dt-filter"
      panelClassName="dt-filter__panel"
      data-testid="capacity-day"
    >
      {(['today', 'yesterday'] as const).map((day) => (
        <MenuItem key={day} aria-pressed={day === value} onClick={() => onChange(day)} data-testid={`capacity-day-${day}`}>
          {t(`capacity.days.${day}`)}
        </MenuItem>
      ))}
    </Menu>
  );
}

function StatCard({ title, children, testId }: { title: string; children: ReactNode; testId: string }) {
  return (
    <section className="stat-card" aria-label={title} data-testid={testId}>
      <h3 className="stat-card__label">{title}</h3>
      {children}
    </section>
  );
}

const seconds = (ms: number, locale: string) => (ms / 1000).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** The day's active users hour by hour, with the measured band (a single series: the title names it). */
function HoursChart({ data, band, title }: { data: CapacityDto['day']; band: [number, number]; title: string }) {
  const { t } = useTranslation();
  const peak = Math.max(0, ...data.hours);
  // the band always shows; above it the scale grows in steps of 20
  const max = Math.max(band[1] + 10, Math.ceil((peak * 1.1) / 20) * 20);
  const ticks = Array.from({ length: Math.floor(max / 20) + 1 }, (_, i) => i * 20);
  const pct = (value: number) => `${(value / max) * 100}%`;
  const hour = (h: number) => `${String(h).padStart(2, '0')}:00`;
  return (
    <figure className="hours-chart" aria-labelledby="hours-chart-title" data-testid="capacity-chart">
      <figcaption className="hours-chart__head">
        <h3 id="hours-chart-title" className="hours-chart__title">
          {title}
        </h3>
        <span className="hours-chart__peak" data-testid="capacity-peak">
          {data.peak ? t('capacity.chart.peak', { hour: hour(data.peak.hour), count: data.peak.users }) : t('capacity.chart.empty')}
        </span>
      </figcaption>
      <div className="hours-chart__plot">
        <div className="hours-chart__axis" aria-hidden="true">
          {ticks.map((tick) => (
            <span key={tick} className="hours-chart__tick" style={{ bottom: pct(tick) }}>
              {tick}
            </span>
          ))}
        </div>
        <div className="hours-chart__area">
          {ticks.map((tick) => (
            <span key={tick} className="hours-chart__grid" style={{ bottom: pct(tick) }} aria-hidden="true" />
          ))}
          <div className="hours-chart__band" style={{ bottom: pct(band[0]), height: pct(band[1] - band[0]) }}>
            <span className="hours-chart__band-label">{t('capacity.chart.band', { low: band[0], high: band[1] })}</span>
          </div>
          {/* each hour is a stop for the keyboard and a line for screen readers: the same details as on hover */}
          <ul className="hours-chart__bars" aria-label={title}>
            {data.hours.map((users, h) => (
              <li
                key={h}
                className="hours-chart__slot"
                tabIndex={0}
                aria-label={t('capacity.chart.hourValue', { hour: hour(h), count: users })}
                data-testid={`capacity-hour-${h}`}
                data-users={users}
              >
                {users > 0 ? <span className="hours-chart__bar" style={{ height: pct(users) }} aria-hidden="true" /> : null}
                <span className="hours-chart__tip" aria-hidden="true">
                  <strong>{t('capacity.chart.people', { count: users })}</strong> {hour(h)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="hours-chart__x" aria-hidden="true">
        {data.hours.map((_, h) => (
          <span key={h}>{h % 3 === 0 ? String(h).padStart(2, '0') : ''}</span>
        ))}
      </div>
    </figure>
  );
}

function ServerRow({ title, hint, value, share, testId }: { title: string; hint?: string; value: ReactNode; share?: { used: number; total: number } | null; testId: string }) {
  const { i18n } = useTranslation();
  const ratio = share && share.total > 0 ? share.used / share.total : null;
  return (
    <tr data-testid={testId}>
      <th scope="row">
        <strong>{title}</strong>
        {hint ? <span className="server-table__hint"> · {hint}</span> : null}
      </th>
      <td className="num">{value}</td>
      <td className="server-table__share">
        {share && ratio !== null ? (
          <span className="server-table__bar">
            <Progress value={share.used} max={share.total} label={title} tone={ratio >= 0.9 ? 'warning' : ratio >= 0.5 ? 'running' : 'success'} />
            <span className="server-table__pct">{formatPercent(share.used, share.total, i18n.language)}</span>
          </span>
        ) : null}
      </td>
    </tr>
  );
}

export function CapacityPanel({ endpoint, day }: { endpoint: string; day: CapacityDay }) {
  const { t, i18n } = useTranslation();
  const zone = useCompanyZone();
  const capacity = useQuery({
    queryKey: ['capacity', endpoint, day],
    queryFn: () => api<CapacityDto>('GET', `${endpoint}?day=${day}`),
    refetchInterval: 15_000,
    placeholderData: keepPreviousData,
  });
  const data = capacity.data;
  if (!data) return <ErrorAlert error={capacity.error} />;
  const locale = i18n.language;
  const { activity, measured, server } = data;
  const gb = (bytes: number) => formatBytes(bytes, locale);
  const statusTone = data.modelServer.status === 'ok' ? 'success' : data.modelServer.status === 'idle' ? 'quiet' : 'danger';
  const ttft = activity.ttftP50Ms;
  const backup = server.lastBackup;
  const backupDay = backup ? zonedDateKey(new Date(backup.at), zone) : null;
  const todayKey = zonedDateKey(new Date(), zone);
  const yesterdayKey = zonedDateKey(new Date(startOfZonedDay(new Date(), zone).getTime() - 1), zone);
  const backupTime = backup ? new Date(backup.at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone: zone }) : '';
  const backupWhen = !backup
    ? null
    : backupDay === todayKey
      ? t('capacity.server.backup.today', { time: backupTime })
      : backupDay === yesterdayKey
        ? t('capacity.server.backup.yesterday', { time: backupTime })
        : new Date(backup.at).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short', timeZone: zone });
  const disk = server.disk;
  return (
    <div className="capacity" data-testid="capacity">
      <ErrorAlert error={capacity.error} />
      <div className="stat-cards">
        <StatCard title={t('capacity.server.title')} testId="capacity-model-server">
          <p className={`stat-card__status stat-card__status--${statusTone}`}>
            <span className="stat-card__dot" aria-hidden="true" />
            {t(`capacity.server.status.${data.modelServer.status}`)}
          </p>
          <p className="stat-card__sub">
            {data.modelServer.model ? `${data.modelServer.model} · ` : ''}
            {t('capacity.server.calls', { count: activity.modelCalls15m, errors: activity.errors15m })}
          </p>
        </StatCard>
        <StatCard title={t('capacity.users.title')} testId="capacity-users">
          <p className="stat-card__value">{activity.activeUsers.toLocaleString(locale)}</p>
          <Progress value={activity.activeUsers} max={measured.chatUsers[1]} label={t('capacity.users.title')} />
          <p className="stat-card__sub">{t('capacity.users.limit', { low: measured.chatUsers[0], high: measured.chatUsers[1] })}</p>
        </StatCard>
        <StatCard title={t('capacity.code.title')} testId="capacity-code">
          <p className="stat-card__value">{activity.codeRuns.toLocaleString(locale)}</p>
          <Progress value={activity.codeRuns} max={measured.codeRuns[1]} label={t('capacity.code.title')} />
          <p className="stat-card__sub">{t('capacity.code.limit', { low: measured.codeRuns[0], high: measured.codeRuns[1] })}</p>
        </StatCard>
        <StatCard title={t('capacity.ttft.title')} testId="capacity-ttft">
          <p className="stat-card__value">{ttft === null ? '—' : t('capacity.ttft.value', { value: seconds(ttft, locale) })}</p>
          <p className="stat-card__sub">
            {activity.ttftP95Ms === null ? t('capacity.ttft.none') : t('capacity.ttft.line', { p95: seconds(activity.ttftP95Ms, locale) })}
          </p>
        </StatCard>
      </div>
      <p className={`capacity-verdict capacity-verdict--${data.verdict}`} role="status" data-testid="capacity-verdict" data-verdict={data.verdict}>
        {data.verdict === 'enough' ? <Icons.check size={16} aria-hidden="true" /> : <Icons.warning size={16} aria-hidden="true" />}
        <span>
          <strong>{t(`capacity.verdict.${data.verdict}`)}</strong> {t('capacity.rule', { low: measured.chatUsers[0] })}
        </span>
      </p>
      <HoursChart data={data.day} band={measured.chatUsers} title={t(`capacity.chart.title.${day}`)} />
      <section className="server-table" aria-labelledby="server-table-title" data-testid="capacity-server">
        <h3 id="server-table-title" className="server-table__title">
          {t('capacity.server.table')}
        </h3>
        <table>
          <tbody>
            {/* GPU memory only when the model server's metrics tell it (D-138) */}
            {server.gpu ? (
              <ServerRow
                title={t('capacity.server.gpu.title')}
                {...(server.gpu.model ? { hint: t('capacity.server.gpu.hint', { model: server.gpu.model }) } : {})}
                value={`${gb(server.gpu.usedBytes)} / ${gb(server.gpu.totalBytes)}`}
                share={{ used: server.gpu.usedBytes, total: server.gpu.totalBytes }}
                testId="server-gpu"
              />
            ) : null}
            {server.memory ? (
              <ServerRow
                title={t('capacity.server.memory.title')}
                hint={t('capacity.server.memory.hint')}
                value={`${gb(server.memory.usedBytes)} / ${gb(server.memory.totalBytes)}`}
                share={{ used: server.memory.usedBytes, total: server.memory.totalBytes }}
                testId="server-memory"
              />
            ) : null}
            <ServerRow
              title={t('capacity.server.disk.title')}
              hint={[
                t('capacity.server.disk.database', { size: gb(disk.databaseBytes) }),
                disk.filesBytes !== null ? t('capacity.server.disk.files', { size: gb(disk.filesBytes) }) : null,
                t('capacity.server.disk.backups', { size: disk.backupsBytes !== null ? gb(disk.backupsBytes) : '—' }),
              ]
                .filter(Boolean)
                .join(' · ')}
              value={disk.freeBytes !== null ? t('capacity.server.disk.free', { size: gb(disk.freeBytes) }) : '—'}
              share={disk.freeBytes !== null && disk.totalBytes ? { used: disk.totalBytes - disk.freeBytes, total: disk.totalBytes } : null}
              testId="server-disk"
            />
            <ServerRow
              title={t('capacity.server.queues.title')}
              hint={t('capacity.server.queues.hint', data.queues)}
              value={(data.queues.interactive + data.queues.background + data.queues.documents).toLocaleString(locale)}
              testId="server-queues"
            />
            <ServerRow
              title={t('capacity.server.sandboxes.title')}
              hint={t('capacity.server.sandboxes.hint', server.sandboxes)}
              value={(server.sandboxes.chat + server.sandboxes.code).toLocaleString(locale)}
              testId="server-sandboxes"
            />
            <ServerRow
              title={t('capacity.server.backup.title')}
              hint={backup ? (backup.kept !== null ? t('capacity.server.backup.kept', { count: backup.kept }) : t('capacity.server.backup.nightly')) : t('capacity.server.backup.none')}
              value={
                backup ? (
                  <span className="server-table__ok">
                    <span className="stat-card__dot" aria-hidden="true" />
                    {backupWhen}
                    {backup.bytes !== null ? ` · ${gb(backup.bytes)}` : ''}
                  </span>
                ) : (
                  '—'
                )
              }
              testId="server-backup"
            />
          </tbody>
        </table>
      </section>
    </div>
  );
}
